import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAlert, usePrompt } from "../components/Confirm";

const STATUS_META: Record<string, { label: string; classes: string }> = {
  enabled: { label: "Enabled",  classes: "bg-liaise-green/20 text-emerald-900" },
  coming:  { label: "Coming",   classes: "bg-liaise-yellow/30 text-amber-900" },
  planned: { label: "Planned",  classes: "bg-slate-100 text-slate-700" },
  retired: { label: "Retired",  classes: "bg-liaise-red/10 text-red-700" },
  hidden:  { label: "Hidden",   classes: "bg-slate-200 text-slate-500" },
};

export function Modules() {
  const qc = useQueryClient();
  const alertModal = useAlert();
  const list = useQuery({ queryKey: ["modules-admin"], queryFn: api.modulesAdmin });
  const [savingKey, setSavingKey] = useState<string | null>(null);

  async function changeStatus(key: string, status: string) {
    setSavingKey(key);
    try {
      await api.moduleUpdate(key, { status });
      await qc.invalidateQueries({ queryKey: ["modules-admin"] });
      await qc.invalidateQueries({ queryKey: ["modules"] });
    } catch (err) {
      await alertModal({ title: "Could not update", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setSavingKey(null); }
  }

  async function toggleApprovalViaRgm(key: string, approval_via_rgm: boolean) {
    setSavingKey(key);
    try {
      await api.moduleUpdate(key, { approval_via_rgm });
      await qc.invalidateQueries({ queryKey: ["modules-admin"] });
      await qc.invalidateQueries({ queryKey: ["modules"] });
    } catch (err) {
      await alertModal({ title: "Could not update", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setSavingKey(null); }
  }

  if (list.isLoading) return <div className="text-slate-500">Loading...</div>;
  const rows: any[] = list.data?.modules ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Modules</h1>
          <p className="text-slate-500 mt-1">
            Each pricing surface is a module with its own rate card, rules, and approval flow.
            Flip a module to <b>Enabled</b> once its UI has been built. <b>Coming</b> tiles are visible to claimants but not clickable. <b>Hidden</b> disappears entirely.
          </p>
        </div>
        <TestEmailsButton />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((m) => {
          const meta = STATUS_META[m.status] ?? STATUS_META.coming;
          return (
            <div key={m.key} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{m.icon}</span>
                    <h2 className="text-lg font-semibold text-liaise-navy">{m.display_name}</h2>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 font-mono">module key: {m.key}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${meta.classes}`}>{meta.label}</span>
              </div>
              {m.description && <p className="text-sm text-slate-600 mt-3">{m.description}</p>}

              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-slate-500">Estimate types</div>
                  <div className="text-slate-800 mt-0.5">{m.type_keys || "—"}</div>
                </div>
                <div>
                  <div className="text-slate-500">Owns</div>
                  <div className="text-slate-800 mt-0.5">
                    {[m.has_rate_card && "rate card", m.has_rules && "rules"].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {["enabled", "coming", "planned", "retired", "hidden"].map((s) => (
                  <button
                    key={s}
                    onClick={() => changeStatus(m.key, s)}
                    disabled={savingKey === m.key || s === m.status}
                    className={[
                      "text-xs px-3 py-1.5 rounded-full border",
                      s === m.status
                        ? "bg-liaise-navy text-white border-liaise-navy"
                        : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700",
                      savingKey === m.key ? "opacity-50" : "",
                    ].join(" ")}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>

              {/* Approval routing: RGM-led vs Commercial-Finance-led. System module skipped. */}
              {m.key !== "system" && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!m.approval_via_rgm}
                      disabled={savingKey === m.key}
                      onChange={(e) => toggleApprovalViaRgm(m.key, e.target.checked)}
                    />
                    <span className="text-sm">
                      <span className="font-medium text-liaise-navy">RGM approval</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {m.approval_via_rgm
                          ? "Approval emails go to the RGM for the home's region (CC: Commercial Finance). RGMs can approve."
                          : "Commercial Finance (James, Russell) approves directly. RGMs cannot approve, only see on CC."}
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Sends all 8 email templates to a chosen address so the formatting can be eyeballed. */
function TestEmailsButton() {
  const alertModal = useAlert();
  const prompt = usePrompt();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const [busy, setBusy] = useState(false);

  async function send() {
    // Default to the user's real mailbox (often the primary, not the _admin alias).
    const r = await prompt({
      title: "Send test emails",
      body: "All 8 templates will be sent to this address with [TEST] in every subject. Defaults to your own mailbox.",
      prompt: {
        label: "Recipient",
        placeholder: "name@liaise.com",
        initial: me.data?.email ?? "",
        required: true,
      },
      confirmLabel: "Send",
    });
    if (!r.ok || !r.value) return;
    setBusy(true);
    try {
      const res = await api.sendTestEmails(r.value);
      await alertModal({
        title: `Sent ${res.sent?.length ?? 0} test emails to ${res.recipient}`,
        body: (
          <div className="text-xs font-mono whitespace-pre-wrap text-slate-700">
            {(res.sent ?? []).join("\n")}
          </div>
        ),
        confirmLabel: "OK",
      });
    } catch (err) {
      await alertModal({ title: "Send failed", body: (err as Error).message, confirmLabel: "OK" });
    } finally { setBusy(false); }
  }

  return (
    <button onClick={send} disabled={busy}
      className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm hover:bg-slate-50 disabled:opacity-50"
      title="Sends every email template to a chosen address so you can eyeball the formatting.">
      {busy ? "Sending..." : "✉️ Send test emails"}
    </button>
  );
}
